<template>
  <div class="chart-container" :class="{ 'chart-container-blur': !hasDreOffersSchedulesResults && variant === 'home' }">
    <apexchart :key="page" ref="chart" :width="width" type="area" :height="variant === 'dre' ? 260 : 170" :options="options" :series="series"></apexchart>
    <mf-icon
      v-if="(variant === 'home' || isIsolated) && metrics.length && !isPdf"
      class="left-icon"
      icon="chevron_left"
      size="20"
      :color="leftArrowDisabled ? 'cinza-l70' : 'cinza-d10'"
      @click="handleClickLeft"
    />
    <mf-icon
      v-if="(variant === 'home' || isIsolated) && metrics.length && !isPdf"
      class="right-icon"
      icon="chevron_right"
      size="20"
      :color="rightArrowDisabled ? 'cinza-l70' : 'cinza-d10'"
      @click="handleClickRight"
    />
  </div>
</template>

<script>
import { abbreviation } from '@/config/helpers.js'
import { DateTime } from 'luxon'
import { mapGetters, mapState } from 'vuex'

export default {
  name: 'IncrementalRevenueChart',
  props: {
    variant: {
      type: String,
      default: 'dre',
      validator: (value) => ['', 'dre', 'home'].includes(value),
    },
    showMockData: {
      type: Boolean,
      default: false,
    },
    analysedPeriod: {
      type: String,
      default: '',
    },
    analysedPeriodWeekly: {
      type: Array,
      default: () => ['', ''],
    },
    isIsolated: {
      type: Boolean,
      default: false,
    },
    isPdf: {
      type: Boolean,
      default: false,
    },
    allMonthMetrics: {
      type: Array,
      default: () => [],
    },
    initialPage: {
      type: Number,
      default: 0,
    },
  },
  data: () => ({
    width: '99%',
    page: 0,
    perPage: 5,
    removeGraphAnimation: false,
  }),
  computed: {
    ...mapGetters(['dreRevenueIncrementMetrics', 'lastUpdate', 'hasDreOffersSchedulesResults']),
    ...mapState({
      lastUpdate: (state) => state.lastUpdate,
    }),
    leftArrowDisabled() {
      return this.page === 0
    },
    rightArrowDisabled() {
      if (this.isIsolated) return this.allMonthMetrics.length === this.page + 1
      return this.currentPageCategories[4] === Number(this.lastUpdateDay)
    },
    metricsLastDay() {
      const analysedPeriodLastDay = Number(DateTime.fromFormat(this.analysedPeriod, 'yyyy-MM').endOf('month').toFormat('yyyyMMdd'))
      const lastUpdated = Number(this.lastUpdate?.last_updated)
      const finalDate = Math.min(lastUpdated, analysedPeriodLastDay).toString()

      return Number(DateTime.fromFormat(finalDate, 'yyyyMMdd').toFormat('dd'))
    },
    isWeeklyAnalysis() {
      return !!this.analysedPeriodWeekly[0]
    },
    metrics() {
      const metrics = this.dreRevenueIncrementMetrics?.periodMetrics?.dailyMetrics
      if (!metrics?.length) return []
      if (this.isIsolated) return this.allMonthMetrics[this.page]
      if (this.isWeeklyAnalysis) return metrics
      return metrics.slice(0, this.metricsLastDay)
    },
    allCategories() {
      const allCategories = this.metrics.map((category) => new Date(category.date).getUTCDate())
      if (allCategories.length === 1) allCategories.unshift(allCategories[0])
      return allCategories
    },
    lastUpdateDay() {
      if (!this.lastUpdate?.last_updated) return 0
      const lastUpdated = this.lastUpdate?.last_updated?.toString()
      return Number(DateTime.fromFormat(lastUpdated, 'yyyyMMdd').toFormat('dd'))
    },
    currentPageCategories() {
      let start = this.page % this.allCategories.length
      let end = start + this.perPage
      return this.allCategories.slice(start, end)
    },
    categories() {
      return this.variant === 'home' ? this.currentPageCategories : this.allCategories
    },
    options() {
      const self = this
      return {
        tooltip: {
          custom: function ({ series, dataPointIndex }) {
            const value = series[0][dataPointIndex]
            const tooltipDate = self.weeklyDates[dataPointIndex] ? self.weeklyDates[dataPointIndex] : self.weeklyDates[dataPointIndex - 1]
            const values = self.metrics.map((item) => item.metrics.incrementalRevenue)
            return values.length === 1 && value === 0
              ? ''
              : `
                <div class='custom-tooltip'>
                  <h1>${tooltipDate}</h1>
                  <p>Fat. incremental: <b>R$${abbreviation(value, { isCurrency: true })}</b></p>
                </div>
              `
          },
        },
        chart: {
          animations: {
            enabled: !this.removeGraphAnimation,
            easing: 'easeinout',
            speed: 800,
            animateGradually: {
              enabled: true,
              delay: 150,
            },
            dynamicAnimation: {
              enabled: true,
              speed: 350,
            },
          },
          type: 'area',
          toolbar: {
            show: false,
          },
          zoom: {
            enabled: false,
          },
        },
        dataLabels: {
          enabled: false,
        },
        stroke: {
          curve: 'smooth',
          width: 1.5,
        },
        colors: ['#0A8533'],
        xaxis: {
          type: 'category',
          categories: this.categories,
          stepSize: false,
          overwriteCategories: undefined,
          labels: {
            show: true,
          },
        },
        yaxis: {
          type: 'numeric',
          tickAmount: this.variant === 'home' ? 3 : 4,
          labels: {
            formatter: function (value) {
              return `R$${abbreviation(value, { isCurrency: true })}`
            },
          },
          forceNiceScale: true,
        },
        fill: {
          type: 'gradient',
          gradient: {
            shadeIntensity: 1,
            opacityFrom: 0.02,
            opacityTo: 0.6,
            stops: [0, 100],
          },
        },
      }
    },
    series() {
      let data
      if (this.variant === 'home') {
        let start = this.page % this.allCategories.length
        let end = start + this.perPage
        data = this.allValues.slice(start, end)
      } else {
        data = this.allValues
      }
      return [
        {
          name: 'Faturamento',
          data: data,
        },
      ]
    },
    allValues() {
      const values = this.metrics.map((item) => item.metrics.incrementalRevenue)
      if (values.length === 1) values.unshift(values[0])
      return values
    },
    weeklyDates() {
      return this.metrics.map((metric) => DateTime.fromISO(metric.date, { zone: 'UTC' }).toFormat('dd/MM/yyyy'))
    },
  },
  watch: {
    page() {
      this.updateSeries()
    },
  },
  mounted() {
    setTimeout(() => {
      this.width = '100%'
    }, 300)
    if (this.variant === 'home' && this.lastUpdateDay > 5) {
      this.page = this.lastUpdateDay - 5 // init at the last page (only variant home)
    } else this.page = this.initialPage
  },
  methods: {
    handleClickLeft() {
      if (this.leftArrowDisabled) return

      this.removeGraphAnimation = true
      if (this.page > 0) this.page--
      this.$emit('change-page', this.page)
    },
    handleClickRight() {
      if (this.rightArrowDisabled) return

      this.removeGraphAnimation = true
      this.page++
      this.$emit('change-page', this.page)
    },
    updateSeries() {
      this.$refs.chart.updateSeries(this.series)
    },
  },
}
</script>

<style lang="scss" scoped>
.chart-container-blur {
  opacity: 0.4;
}

.chart-container {
  flex: 1;
  position: relative;

  .left-icon {
    position: absolute;
    bottom: 22px;
    left: 40px;
    cursor: pointer;
  }

  .right-icon {
    position: absolute;
    bottom: 23px;
    right: -26px;
    cursor: pointer;
  }
}

::v-deep .custom-tooltip {
  padding: 4px 8px;
  opacity: 1 !important;
  border-radius: 8px;

  h1 {
    font-size: 16px;
    color: $cinza-d20;
  }

  p {
    color: $cinza-0;
  }

  b {
    color: $cinza-d20;
  }
}
</style>
